import { DeleteOutlined } from '@ant-design/icons';
import {
	Alert,
	Button,
	Input,
	Space,
	Table,
	Tabs,
	Tag,
	Tooltip,
	Typography,
} from 'antd';
import type { TabsProps } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { FC, ReactNode } from 'react';
import {
	Navigate,
	useNavigate,
	useParams,
} from 'react-router';
import { APPS_TABS } from '@constants/routes/navItems';
import { AUTH_SERVICES_BASE_URL } from '@constants/url';
import PageContainer from '@ui/PageContainer';
import PageTitle from '@ui/PageTitle';
import Section from '@ui/Section';
import AppPermissionForm from './AppPermissionForm';
import useAppsPage, { type AppAssignment } from './useAppsPage';
import styles from './styles.module.scss';

const { Text } = Typography;

/**
 * Filter options are the distinct values actually present in the column, so
 * the dropdown never offers something that would match nothing.
 */
const filtersFor = (items: AppAssignment[], key: keyof AppAssignment) =>
	[...new Set(items.map((item) => item[key]))].sort().map((value) => ({ text: value, value }));

const getColumns = (
	items: AppAssignment[],
	onDelete: (assignment: AppAssignment) => void,
	isDeleting: (assignment: AppAssignment) => boolean
): ColumnsType<AppAssignment> => [
	{
		title: 'Object type',
		dataIndex: 'objectType',
		key: 'objectType',
		filters: filtersFor(items, 'objectType'),
		onFilter: (value, assignment) => assignment.objectType === value,
	},
	{
		title: 'Object ID',
		dataIndex: 'objectId',
		key: 'objectId',
		render: (objectId: string) => (
			<Space size={4}>
				{/* antd's copyable handles the clipboard and the copied state. */}
				<Text
					copyable={{ text: objectId, tooltips: ['Copy Object ID', 'Copied'] }}
					data-testid={`apps-copy-${objectId}`}
				/>
				<Tag className={styles.objectId}>{objectId}</Tag>
			</Space>
		),
	},
	{
		title: 'Role',
		dataIndex: 'role',
		key: 'role',
		filters: filtersFor(items, 'role'),
		onFilter: (value, assignment) => assignment.role === value,
	},
	{
		title: '',
		key: 'delete',
		width: 56,
		align: 'right',
		render: (_, assignment) => (
			<Tooltip title="Delete assignment">
				<Button
					type="text"
					danger
					size="small"
					icon={<DeleteOutlined />}
					loading={isDeleting(assignment)}
					onClick={() => onDelete(assignment)}
					aria-label={`Delete ${assignment.role} on ${assignment.objectId}`}
					data-testid={`apps-delete-${assignment.objectId}-${assignment.role}`}
				/>
			</Tooltip>
		),
	},
];

const [DEFAULT_TAB] = APPS_TABS;

const AppsPage: FC = () => {
	// The open tab is the URL, so it survives a reload and can be linked to.
	const { tab } = useParams();
	const navigate = useNavigate();

	const {
		search,
		items,
		roles,
		isFetching,
		hasSearched,
		confirmDeleteAssignment,
		isDeleting,
	} = useAppsPage();

	// The tab bar sits on the page background, so each panel brings its own white
	// surface. Each tab's label is its own heading, so no panel carries one.
	const panels: Record<string, ReactNode> = {
		assignments: (
			<Section white spacious>
				<Input.Search
					placeholder="Enter Client ID"
					enterButton="Search"
					allowClear
					loading={isFetching}
					disabled={!AUTH_SERVICES_BASE_URL}
					onSearch={search}
					data-testid="apps-cid"
				/>
				{roles.length > 0 && (
					<Alert
						type="info"
						showIcon
						className={styles.rolesAlert}
						message="High level permissions"
						description={
							<Space
								wrap
								size={4}
								data-testid="apps-roles"
							>
								{roles.map((role) => (
									<Tag
										key={role}
										className={styles.roleTag}
									>
										{role}
									</Tag>
								))}
							</Space>
						}
					/>
				)}
				{items.length > 0 && (
					<Table<AppAssignment>
						size="small"
						columns={getColumns(items, confirmDeleteAssignment, isDeleting)}
						dataSource={items}
						pagination={false}
						// No id on a row, so identify it by what it is.
						rowKey={({ objectType, objectId, role }) =>
							`${objectType}-${objectId}-${role}`
						}
						data-testid="apps-results"
					/>
				)}
				{/* The table is hidden when empty, so a search that found nothing
				    still needs to say so. */}
				{hasSearched && !isFetching && !items.length && !roles.length && (
					<Text
						type="secondary"
						data-testid="apps-empty"
					>
						No assignments found
					</Text>
				)}
			</Section>
		),
		permissions: (
			<Section white spacious>
				<AppPermissionForm />
			</Section>
		),
	};

	// Order and labels come from the nav metadata, so the tab bar and the
	// breadcrumb for the same tab can't disagree.
	const tabs: TabsProps['items'] = APPS_TABS.map(({ segment, label }) => ({
		key: segment,
		label,
		children: panels[segment],
	}));

	// Bare /apps has no tab, and a hand-typed one may not exist. Either way the
	// URL should name the tab that's actually open.
	if (!tab || !(tab in panels)) {
		return (
			<Navigate
				to={`/apps/${DEFAULT_TAB.segment}`}
				replace
			/>
		);
	}

	return (
		<PageContainer>
			<PageTitle>Apps</PageTitle>
			<Tabs
				activeKey={tab}
				items={tabs}
				onChange={(key) => navigate(`/apps/${key}`)}
				data-testid="apps-tabs"
			/>
		</PageContainer>
	);
};

export default AppsPage;
